import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X, Tag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { Logo } from "./Logo";

export const Navbar = () => {
  const { count, setOpen, cartEnabled, promo } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [promoClosed, setPromoClosed] = useState(
    () => sessionStorage.getItem("promo_closed") === "true"
  );

  const go = (id) => {
    setMenuOpen(false);
    if (id === "catalogo") {
      navigate("/catalogo");
      return;
    }
    if (location.pathname !== "/") {
      navigate("/#" + id);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const goHome = () => {
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      return;
    }
    document.getElementById("hero")?.scrollIntoView({ behavior: "smooth" });
  };

  const closePromo = () => {
    sessionStorage.setItem("promo_closed", "true");
    setPromoClosed(true);
  };

  const showPromo = promo && promo.enabled && promo.text && !promoClosed;

  const links = [
    { id: "catalogo", label: "Catalogo" },
    { id: "manifesto", label: "Manifesto" },
    { id: "contatti", label: "Contatti" },
  ];

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50 bg-[#0D110D]/85 backdrop-blur-md border-b border-white/10"
      data-testid="navbar"
    >
      <AnimatePresence>
        {showPromo && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-[#D47738] text-[#0D110D] overflow-hidden"
            data-testid="promo-banner"
          >
            <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-2 flex items-center justify-center gap-3 text-center relative">
              <Tag className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
              <p className="font-mono-accent text-[11px] md:text-xs uppercase tracking-[0.15em] font-semibold" data-testid="promo-text">
                {promo.text}
                {promo.link_url && promo.link_label && (
                  <a
                    href={promo.link_url}
                    className="underline underline-offset-2 ml-2 hover:opacity-70 transition-opacity"
                    data-testid="promo-link"
                  >
                    {promo.link_label}
                  </a>
                )}
              </p>
              <button
                onClick={closePromo}
                className="absolute right-5 md:right-12 hover:opacity-60 transition-opacity"
                aria-label="Chiudi"
                data-testid="promo-close-btn"
              >
                <X className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-[1600px] mx-auto px-5 md:px-12 h-20 flex items-center justify-between relative">
        <button onClick={goHome} data-testid="logo-btn" className="text-left">
          <Logo />
        </button>

        <nav className="hidden md:flex items-center gap-10 font-mono-accent text-xs uppercase tracking-[0.2em]">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="text-[#E8E2D2]/70 hover:text-[#D47738] transition-colors duration-300"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {cartEnabled && (
            <button
              onClick={() => setOpen(true)}
              className="relative flex items-center gap-2 text-[#E8E2D2] hover:text-[#D47738] transition-colors duration-300"
              data-testid="cart-drawer-trigger"
            >
              <ShoppingBag className="w-6 h-6" strokeWidth={1.5} />
              {count > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-[#D47738] text-[#0D110D] text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full font-mono-accent"
                  data-testid="cart-count"
                >
                  {count}
                </span>
              )}
            </button>
          )}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden text-[#E8E2D2]"
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-[#0D110D]"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-5 font-mono-accent text-sm uppercase tracking-[0.2em]">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left text-[#E8E2D2]/80 hover:text-[#D47738] transition-colors"
                  data-testid={`mobile-nav-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
